import { useContext, useState } from "react";
import { Button, Avatar, Divider, Timeline, Tag, Row, Col, Typography, Tooltip } from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    UserOutlined,
    HistoryOutlined,
    DownOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import { FaTicketAlt, FaTrophy } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

import { EAwardStatus } from "../../../../../enums";
import { IAward } from "../../../../../interfaces";
import { awardImagesUrl } from "../../../../../services/urls";
import { convertDataUSParaBrasilEHora, convertMoedaBRDoJSNovo } from "../../../../../utils/Conversores";
import { ThemeContext } from "../../../../../contexts/ThemeContext";

const { Text } = Typography;

interface IProps {
    award: IAward;
    onChangeStatus: (award: IAward, isValid: boolean) => void;
    onVerUsuario: (appUserId: number) => void;
}

const AccordionAward = ({
    award,
    onChangeStatus,
    onVerUsuario,
}: IProps) => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const [isExpanded, setIsExpanded] = useState(false);

    // Get the history entries sorted by ID
    const historyList = award?.award_history ? [...award.award_history].sort((a, b) => a.id - b.id) : [];
    const lastAwardHistory = historyList.at(-1);
    const photo = Array.isArray(award.images) && award.images.length > 0 ? award.images[0].img : null;

    const winnerUser = lastAwardHistory?.app_user || award.app_user;
    const winnerUserAuto = lastAwardHistory?.app_user_auto || award.app_user_auto;
    const winnerId = winnerUser?.id || (winnerUser as any)?.app_user_id || winnerUserAuto?.id;
    const winningTicketId = lastAwardHistory?.winning_ticket_id || award.winning_ticket_id || award.winning_ticket_auto_id;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ marginBottom: 12 }}
        >
            <div
                onClick={() => {
                    if (historyList.length > 0) {
                        setIsExpanded(!isExpanded);
                    }
                }}
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? (isExpanded ? '#1765ad' : '#262626') : (isExpanded ? '#91caff' : '#eaedf1')}`,
                    boxShadow: isDark
                        ? '0 2px 10px rgba(0, 0, 0, 0.3)'
                        : '0 1px 4px rgba(0, 0, 0, 0.03)',
                    padding: '14px 18px',
                    transition: 'all 0.2s ease',
                    cursor: historyList.length > 0 ? 'pointer' : 'default',
                }}
                onMouseEnter={(e) => {
                    if (historyList.length > 0 && !isExpanded) {
                        e.currentTarget.style.borderColor = isDark ? '#3b82f6' : '#93c5fd';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isExpanded) {
                        e.currentTarget.style.borderColor = isDark ? '#262626' : '#eaedf1';
                    }
                }}
            >
                {/* Main Unified Row */}
                <Row gutter={[16, 12]} align="middle">
                    {/* Left: Prize Information */}
                    <Col xs={24} md={9} lg={8}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            {photo ? (
                                <Avatar
                                    shape="square"
                                    size={52}
                                    src={`${awardImagesUrl}/${photo}`}
                                    style={{
                                        borderRadius: 8,
                                        objectFit: "cover",
                                        border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                        flexShrink: 0,
                                    }}
                                />
                            ) : (
                                <Avatar
                                    shape="square"
                                    size={52}
                                    icon={<FaTrophy size={22} color="#eab308" />}
                                    style={{
                                        backgroundColor: isDark ? '#1f1f1f' : '#f8fafc',
                                        borderRadius: 8,
                                        border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                />
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    {award.order != null && (
                                        <Tag color="gold" style={{ borderRadius: 4, fontWeight: 700, margin: 0, fontSize: 11, padding: '0 6px' }}>
                                            {award.order}º Prêmio
                                        </Tag>
                                    )}
                                    <Text strong style={{ fontSize: 14, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                        {award.description || "Sem descrição"}
                                    </Text>
                                </div>

                                <div style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <span>
                                        Valor:{" "}
                                        <strong style={{ color: '#10b981', fontSize: 14, fontWeight: 700 }}>
                                            R$ {convertMoedaBRDoJSNovo(award.value)}
                                        </strong>
                                    </span>
                                    {award.social_institution_value && Number(award.social_institution_value) > 0 ? (
                                        <span>
                                            Inst. Social:{" "}
                                            <strong style={{ color: '#1677ff', fontSize: 13, fontWeight: 700 }}>
                                                R$ {convertMoedaBRDoJSNovo(award.social_institution_value)}
                                            </strong>
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Right: Latest Apuração, Winner & 3-Line Action Column */}
                    <Col xs={24} md={15} lg={16}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 14,
                            }}
                        >
                            {lastAwardHistory ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 180 }}>
                                    {/* Top info: Status tag, attempt, date and ticket */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <Tag
                                            color={
                                                lastAwardHistory.status === EAwardStatus.valid
                                                    ? "success"
                                                    : lastAwardHistory.status === EAwardStatus.invalid
                                                        ? "error"
                                                        : "processing"
                                            }
                                            style={{ borderRadius: 4, fontWeight: 600, margin: 0, fontSize: 11 }}
                                        >
                                            {lastAwardHistory.status}
                                        </Tag>

                                        <span style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                                            {lastAwardHistory.attempt_number}ª Apuração • {convertDataUSParaBrasilEHora(lastAwardHistory.created_at)}
                                        </span>

                                        {winningTicketId && (
                                            <Tag
                                                color="blue"
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    borderRadius: 4,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    padding: '0 6px',
                                                    margin: 0,
                                                }}
                                            >
                                                <FaTicketAlt size={10} />
                                                #{winningTicketId}
                                            </Tag>
                                        )}
                                    </div>

                                    {/* Winner info with inline view user button (no CPF / phone) */}
                                    {winnerUser && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            <Text type="secondary" style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                                                Ganhador:
                                            </Text>
                                            <span style={{ fontWeight: 700, fontSize: 13, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                                {winnerUser.name}
                                            </span>
                                            {winnerId && (
                                                <Tooltip title="Ver usuário no aplicativo">
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        icon={<UserOutlined style={{ fontSize: 11 }} />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onVerUsuario(winnerId);
                                                        }}
                                                        style={{
                                                            padding: '0 4px',
                                                            height: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 3,
                                                        }}
                                                    >
                                                        Ver usuário
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {winnerUser.social_institution_name && (
                                                <span style={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#475569' }}>
                                                    • Inst: <strong>{winnerUser.social_institution_name}</strong>
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {winnerUserAuto && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            <Text type="secondary" style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                                                Ganhador:
                                            </Text>
                                            <span style={{ fontWeight: 700, fontSize: 13, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                                {winnerUserAuto.name}
                                            </span>
                                            {winnerId && (
                                                <Tooltip title="Ver usuário no aplicativo">
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        icon={<UserOutlined style={{ fontSize: 11 }} />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onVerUsuario(winnerId);
                                                        }}
                                                        style={{
                                                            padding: '0 4px',
                                                            height: 20,
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 3,
                                                        }}
                                                    >
                                                        Ver usuário
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    )}

                                    {lastAwardHistory?.taxe_auto && (
                                        <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                                            Código do Imóvel: <strong style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{lastAwardHistory.taxe_auto.propertyCode}</strong>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: isDark ? '#8c8c8c' : '#94a3b8', fontSize: 13 }}>
                                    <ClockCircleOutlined />
                                    <span>Nenhuma apuração realizada ainda</span>
                                </div>
                            )}

                            {/* Actions on far right in 3 vertical lines */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 5,
                                    width: 115,
                                    flexShrink: 0,
                                    alignItems: 'stretch',
                                }}
                            >
                                {lastAwardHistory?.status === EAwardStatus.underAnalysis && (
                                    <>
                                        {/* Line 1: Validar */}
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<CheckCircleOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChangeStatus(award, true);
                                            }}
                                            style={{
                                                backgroundColor: "#10b981",
                                                borderColor: "#10b981",
                                                borderRadius: 6,
                                                fontWeight: 600,
                                                fontSize: 11,
                                                height: 24,
                                                width: '100%',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 4,
                                            }}
                                        >
                                            Validar
                                        </Button>

                                        {/* Line 2: Invalidar */}
                                        <Button
                                            type="primary"
                                            danger
                                            size="small"
                                            icon={<CloseCircleOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChangeStatus(award, false);
                                            }}
                                            style={{
                                                borderRadius: 6,
                                                fontWeight: 600,
                                                fontSize: 11,
                                                height: 24,
                                                width: '100%',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 4,
                                            }}
                                        >
                                            Invalidar
                                        </Button>
                                    </>
                                )}

                                {/* Line 3: History indicator */}
                                {historyList.length > 0 && (
                                    <Tooltip title={isExpanded ? "Clique para recolher histórico" : "Clique para ver histórico completo"}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 4,
                                                color: isDark ? '#94a3b8' : '#64748b',
                                                fontSize: 11,
                                                padding: '3px 6px',
                                                borderRadius: 6,
                                                background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                                                border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                                                transition: 'all 0.2s',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <HistoryOutlined style={{ fontSize: 11, color: '#1677ff' }} />
                                            <span style={{ fontWeight: 600 }}>{historyList.length} {historyList.length === 1 ? 'apuração' : 'apurações'}</span>
                                            <DownOutlined
                                                style={{
                                                    fontSize: 9,
                                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease',
                                                }}
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Full History Timeline (Expandable on click) */}
                <AnimatePresence>
                    {isExpanded && historyList.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            style={{ overflow: 'hidden' }}
                        >
                            <Divider
                                orientation="left"
                                style={{
                                    margin: '14px 0 12px 0',
                                    borderColor: isDark ? '#2d2d2d' : '#e2e8f0',
                                    color: isDark ? '#94a3b8' : '#64748b',
                                    fontSize: 12,
                                    fontWeight: 500,
                                }}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <HistoryOutlined />
                                    Histórico Completo de Apurações
                                </span>
                            </Divider>

                            <div style={{ padding: '2px 4px 0 4px' }}>
                                <Timeline
                                    mode="left"
                                    items={[...historyList].sort((a, b) => b.id - a.id).map((h) => {
                                        const hWinnerUser = h.app_user;
                                        const hWinnerUserAuto = h.app_user_auto;
                                        const hWinnerId = hWinnerUser?.id || (hWinnerUser as any)?.app_user_id || hWinnerUserAuto?.id;

                                        return {
                                            color:
                                                h.status === EAwardStatus.valid
                                                    ? "green"
                                                    : h.status === EAwardStatus.invalid
                                                        ? "red"
                                                        : "blue",
                                            label: (
                                                <span style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>
                                                    {convertDataUSParaBrasilEHora(h.created_at)}
                                                </span>
                                            ),
                                            children: (
                                                <div
                                                    style={{
                                                        background: isDark ? '#1a1a1a' : '#f8fafc',
                                                        border: `1px solid ${isDark ? '#2d2d2d' : '#e2e8f0'}`,
                                                        borderRadius: 8,
                                                        padding: '10px 14px',
                                                        marginBottom: 6,
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                                                        <Text strong style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
                                                            {h.attempt_number}ª Tentativa
                                                        </Text>
                                                        <Tag
                                                            color={
                                                                h.status === EAwardStatus.valid
                                                                    ? "success"
                                                                    : h.status === EAwardStatus.invalid
                                                                        ? "error"
                                                                        : "processing"
                                                            }
                                                            style={{ borderRadius: 4, fontWeight: 600, margin: 0, fontSize: 11 }}
                                                        >
                                                            {h.status}
                                                        </Tag>
                                                    </div>

                                                    {h.winning_ticket_id && (
                                                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                                                            <Text type="secondary" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Bilhete premiado: </Text>
                                                            <Tag color="blue" style={{ borderRadius: 4, fontWeight: 600, margin: 0 }}>
                                                                #{h.winning_ticket_id}
                                                            </Tag>
                                                        </div>
                                                    )}

                                                    {hWinnerUser && (
                                                        <div style={{ fontSize: 13, marginBottom: 4, color: isDark ? '#e2e8f0' : '#334155' }}>
                                                            <Text type="secondary" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Ganhador: </Text>
                                                            <span style={{ fontWeight: 700, marginRight: 6 }}>{hWinnerUser.name}</span>
                                                            {hWinnerId && (
                                                                <Button
                                                                    size="small"
                                                                    type="link"
                                                                    icon={<UserOutlined style={{ fontSize: 11 }} />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onVerUsuario(hWinnerId);
                                                                    }}
                                                                    style={{
                                                                        padding: '0 4px',
                                                                        height: 20,
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 3,
                                                                    }}
                                                                >
                                                                    Ver usuário
                                                                </Button>
                                                            )}
                                                            {hWinnerUser.social_institution_name ? ` • Instituição: ${hWinnerUser.social_institution_name}` : ''}
                                                        </div>
                                                    )}

                                                    {hWinnerUserAuto && (
                                                        <div style={{ fontSize: 13, marginBottom: 4, color: isDark ? '#e2e8f0' : '#334155' }}>
                                                            <Text type="secondary" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Ganhador: </Text>
                                                            <span style={{ fontWeight: 700, marginRight: 6 }}>{hWinnerUserAuto.name}</span>
                                                            {hWinnerId && (
                                                                <Button
                                                                    size="small"
                                                                    type="link"
                                                                    icon={<UserOutlined style={{ fontSize: 11 }} />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onVerUsuario(hWinnerId);
                                                                    }}
                                                                    style={{
                                                                        padding: '0 4px',
                                                                        height: 20,
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 3,
                                                                    }}
                                                                >
                                                                    Ver usuário
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}

                                                    {h.taxe_auto && (
                                                        <div style={{ fontSize: 13, marginBottom: 4, color: isDark ? '#e2e8f0' : '#334155' }}>
                                                            <Text type="secondary" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Código do Imóvel: </Text>
                                                            <strong>{h.taxe_auto.propertyCode}</strong>
                                                        </div>
                                                    )}

                                                    {h.observation && (
                                                        <div
                                                            style={{
                                                                background: isDark ? '#262626' : '#f1f5f9',
                                                                padding: '6px 10px',
                                                                borderRadius: 6,
                                                                fontSize: 12,
                                                                color: isDark ? '#cbd5e1' : '#475569',
                                                                marginTop: 6,
                                                            }}
                                                        >
                                                            <strong>Observação:</strong> {h.observation}
                                                        </div>
                                                    )}
                                                </div>
                                            ),
                                        };
                                    })}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default AccordionAward;
